import { useState, useEffect, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import { NotionPagePicker } from '@/components/integrations/notion-page-picker'
import { knowledgeBaseService } from '@/services/knowledge-base'
import {
  SYNC_FREQUENCY_OPTIONS,
  formatSyncDate,
  getSyncStatusLabel,
  getSyncStatusColor,
  type SyncFrequency,
} from '@/schemas/notion-sync'
import {
  FileText,
  RefreshCw,
  Loader2,
  Clock,
  Layers,
  AlertCircle,
} from 'lucide-react'

// Notion logo SVG component
function NotionLogo({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M4.459 4.208c.746.606 1.026.56 2.428.466l13.215-.793c.28 0 .047-.28-.046-.326L17.86 1.968c-.42-.326-.98-.7-2.055-.607L3.01 2.295c-.466.046-.56.28-.373.466l1.822 1.447zm.793 3.08v13.904c0 .747.373 1.027 1.214.98l14.523-.84c.84-.046.933-.56.933-1.167V6.354c0-.606-.233-.933-.746-.886l-15.177.887c-.56.046-.747.326-.747.933zm14.337.746c.093.42 0 .84-.42.887l-.7.14v10.264c-.607.327-1.167.514-1.634.514-.746 0-.933-.234-1.493-.934l-4.577-7.186v6.952l1.446.327s0 .84-1.167.84l-3.22.187c-.093-.187 0-.653.327-.746l.84-.233V9.854L7.822 9.76c-.094-.42.14-1.026.793-1.073l3.456-.233 4.764 7.279v-6.44l-1.213-.14c-.094-.513.28-.886.746-.933l3.221-.186zM2.17 1.4L15.984.14c1.682-.14 2.102.094 2.802.607l3.876 2.707c.467.326.607.7.607 1.167v16.756c0 1.027-.373 1.634-1.68 1.727l-15.458.933c-.98.047-1.447-.093-1.96-.7l-3.127-4.057c-.56-.746-.793-1.306-.793-1.96V2.942c0-.839.374-1.54 1.727-1.633l.193.093z" />
    </svg>
  )
}

interface SelectedPage {
  id: string
  title: string
}

interface NotionSyncConfigDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  organizationId: string
  projectId: string
  knowledgeBaseId: string
  knowledgeBaseName: string
  workspaceName?: string
  onSyncConfigured?: () => void
  onError?: (message: string) => void
}

export function NotionSyncConfigDialog({
  open,
  onOpenChange,
  organizationId,
  projectId,
  knowledgeBaseId,
  knowledgeBaseName,
  workspaceName,
  onSyncConfigured,
  onError,
}: NotionSyncConfigDialogProps) {
  // State
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [isSyncing, setIsSyncing] = useState(false)
  const [pagePickerOpen, setPagePickerOpen] = useState(false)
  const [internalError, setInternalError] = useState<string | null>(null)

  // Config state
  const [selectedPages, setSelectedPages] = useState<SelectedPage[]>([])
  const [frequency, setFrequency] = useState<SyncFrequency>('manual')
  const [includeSubpages, setIncludeSubpages] = useState(true)
  const [lastSyncAt, setLastSyncAt] = useState<string | null>(null)
  const [syncStatus, setSyncStatus] = useState<string>('idle')

  // Load existing config
  const loadConfig = useCallback(async () => {
    try {
      setIsLoading(true)
      const config = await knowledgeBaseService.getNotionSyncConfig(
        projectId,
        knowledgeBaseId
      )

      if (config.enabled && config.page_ids.length > 0) {
        // Map page_ids with page_titles
        const pages = config.page_ids.map((id, index) => ({
          id,
          title: config.page_titles[index] || id,
        }))
        setSelectedPages(pages)
      }
      setFrequency((config.frequency as SyncFrequency) || 'manual')
      setIncludeSubpages(config.include_subpages)
      setLastSyncAt(config.last_sync_at)
      setSyncStatus(config.sync_status)
    } catch (err) {
      console.error('Failed to load sync config:', err)
      // Not an error - config might not exist yet
    } finally {
      setIsLoading(false)
    }
  }, [projectId, knowledgeBaseId])

  useEffect(() => {
    if (open) {
      loadConfig()
    }
  }, [open, loadConfig])

  const handlePageSelect = (pages: SelectedPage[]) => {
    setSelectedPages(pages)
  }

  const handleSave = async () => {
    setInternalError(null)

    if (selectedPages.length === 0) {
      setInternalError('Selecione pelo menos uma pagina do Notion')
      return
    }

    try {
      setIsSaving(true)
      await knowledgeBaseService.configureNotionSync(projectId, knowledgeBaseId, {
        enabled: true,
        page_ids: selectedPages.map((p) => p.id),
        frequency,
        include_subpages: includeSubpages,
      })

      onSyncConfigured?.()
      onOpenChange(false)
    } catch (err) {
      console.error('Failed to save config:', err)
      const message = 'Falha ao salvar configuracao'
      setInternalError(message)
      onError?.(message)
    } finally {
      setIsSaving(false)
    }
  }

  const handleSyncNow = async () => {
    setInternalError(null)

    if (selectedPages.length === 0) {
      setInternalError('Configure as paginas antes de sincronizar')
      return
    }

    try {
      setIsSyncing(true)

      // Save config first
      await knowledgeBaseService.configureNotionSync(projectId, knowledgeBaseId, {
        enabled: true,
        page_ids: selectedPages.map((p) => p.id),
        frequency,
        include_subpages: includeSubpages,
      })

      // Trigger sync
      const result = await knowledgeBaseService.triggerNotionSync(
        projectId,
        knowledgeBaseId
      )

      setSyncStatus(result.status)
      onSyncConfigured?.()
    } catch (err) {
      console.error('Failed to trigger sync:', err)
      const message = 'Falha ao iniciar sincronizacao'
      setInternalError(message)
      onError?.(message)
    } finally {
      setIsSyncing(false)
    }
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl bg-gray-900 border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <NotionLogo className="h-5 w-5" />
              Configurar Sync Notion
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Configure a sincronizacao de paginas do Notion para{' '}
              <span className="text-gray-200 font-medium">{knowledgeBaseName}</span>
            </DialogDescription>
          </DialogHeader>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Error Alert */}
              {internalError && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/20">
                  <AlertCircle className="h-4 w-4 text-red-500" />
                  <span className="text-sm text-red-400">{internalError}</span>
                </div>
              )}

              {/* Notion Workspace */}
              {workspaceName && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                  <NotionLogo className="h-4 w-4 text-emerald-500" />
                  <span className="text-sm text-emerald-400">
                    Conectado a {workspaceName}
                  </span>
                </div>
              )}

              {/* Page Selection */}
              <div className="space-y-2">
                <Label className="text-gray-200 flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Paginas do Notion
                </Label>
                <div className="flex items-center gap-2">
                  <div
                    className={`flex-1 flex items-center gap-2 px-3 py-2.5 rounded-md border ${
                      selectedPages.length > 0
                        ? 'bg-gray-800 border-gray-600'
                        : 'bg-gray-800/50 border-gray-700 border-dashed'
                    }`}
                  >
                    {selectedPages.length > 0 ? (
                      <div className="flex flex-wrap gap-1.5">
                        {selectedPages.slice(0, 3).map((page) => (
                          <Badge
                            key={page.id}
                            variant="secondary"
                            className="text-xs bg-gray-700"
                          >
                            {page.title}
                          </Badge>
                        ))}
                        {selectedPages.length > 3 && (
                          <Badge variant="secondary" className="text-xs bg-gray-700">
                            +{selectedPages.length - 3}
                          </Badge>
                        )}
                      </div>
                    ) : (
                      <span className="text-sm text-gray-500">
                        Nenhuma pagina selecionada
                      </span>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => setPagePickerOpen(true)}
                    className="border-gray-600 hover:bg-gray-800"
                  >
                    {selectedPages.length > 0 ? 'Alterar' : 'Selecionar'}
                  </Button>
                </div>
              </div>

              {/* Include Subpages */}
              <div className="flex items-center justify-between px-3 py-3 bg-gray-800/30 rounded-lg">
                <div className="flex items-center gap-3">
                  <Layers className="h-4 w-4 text-gray-400" />
                  <div>
                    <Label className="text-gray-200">Incluir subpaginas</Label>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Sincronizar paginas filhas das paginas selecionadas
                    </p>
                  </div>
                </div>
                <Checkbox
                  checked={includeSubpages}
                  onCheckedChange={(checked) => setIncludeSubpages(checked === true)}
                  className="h-5 w-5 border-gray-500"
                />
              </div>

              {/* Sync Frequency */}
              <div className="space-y-2">
                <Label className="text-gray-200 flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Frequencia de Sincronizacao
                </Label>
                <Select value={frequency} onValueChange={(v) => setFrequency(v as SyncFrequency)}>
                  <SelectTrigger className="bg-gray-800 border-gray-600">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700">
                    {SYNC_FREQUENCY_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Sync Status */}
              {lastSyncAt && (
                <div className="flex items-center justify-between px-3 py-2 bg-gray-800/30 rounded-md">
                  <span className="text-sm text-gray-400">Ultima sincronizacao:</span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-300">
                      {formatSyncDate(lastSyncAt)}
                    </span>
                    <Badge variant={getSyncStatusColor(syncStatus)}>
                      {getSyncStatusLabel(syncStatus)}
                    </Badge>
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="ghost"
              onClick={() => onOpenChange(false)}
              className="text-gray-400"
            >
              Cancelar
            </Button>
            <Button
              variant="outline"
              onClick={handleSyncNow}
              disabled={isSyncing || isSaving || selectedPages.length === 0}
              className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10"
            >
              {isSyncing ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              Sincronizar Agora
            </Button>
            <Button
              onClick={handleSave}
              disabled={isSaving || isSyncing}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {isSaving ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Salvar Configuracao
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <NotionPagePicker
        open={pagePickerOpen}
        onOpenChange={setPagePickerOpen}
        organizationId={organizationId}
        selectedPages={selectedPages}
        onSelect={handlePageSelect}
      />
    </>
  )
}
